import {Button, Menu, MenuItem} from '@mui/material';
import {useEffect, useState} from 'react';
import {OrderStatus, OrderStatusUpdateRequestDTO} from '../../../api/models';
import {updateOrderStatus} from '../../../api/order';

interface OrderStatusMenuProps {
    orderStatus: OrderStatus;
    order: {id: string};
    reloadOrderDetails: () => void;
}

export const OrderStatusMenu: React.FC<OrderStatusMenuProps> = ({orderStatus, order, reloadOrderDetails}) => {
    const [availableOptions, setAvailableOptions] = useState<OrderStatus[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    useEffect(() => {
        getAvailableOptions(orderStatus);
    }, [orderStatus]);

    const getAvailableOptions = (orderStatus: OrderStatus) => {
        switch (orderStatus) {
            case 'CREATED':
                setAvailableOptions([OrderStatus.PAYMENT_CONFIRMED]);
                break;
            case 'PAYMENT_CONFIRMED':
                setAvailableOptions([OrderStatus.ORDER_CONFIRMED, OrderStatus.ORDER_DECLINED]);
                break;
            case 'ORDER_CONFIRMED':
                setAvailableOptions([OrderStatus.ORDER_COMPLETED]);
                break;
            case 'ORDER_DECLINED':
                setAvailableOptions([OrderStatus.PAYMENT_REFUNDED]);
                break;
            case 'PAYMENT_REFUNDED':
                setAvailableOptions([OrderStatus.ORDER_CANCELLED]);
                break;
            default:
                return [];
        }
    };

    const getSelectedOptions = (orderStatus: OrderStatus): string | undefined => {
        switch (orderStatus) {
            case OrderStatus.CREATED:
                return 'Order Created';
            case OrderStatus.PAYMENT_CONFIRMED:
                return 'Payment Confirmed';
            case OrderStatus.ORDER_CONFIRMED:
                return 'Order Confirmed';
            case OrderStatus.ORDER_DECLINED:
                return 'Order Declined';
            case OrderStatus.PAYMENT_REFUNDED:
                return 'Payment Refunded';
            case OrderStatus.ORDER_COMPLETED:
                return 'Order Completed';
            case OrderStatus.ORDER_CANCELLED:
                return 'Order Cancelled';
            default:
                return undefined;
        }
    };

    const handleOrderUpdate = async (orderId: string, newOrderStatus: OrderStatus) => {
        const orderStatusUpdateRequestDTO: OrderStatusUpdateRequestDTO = {
            orderId,
            status: newOrderStatus,
        };
        await updateOrderStatus(orderStatusUpdateRequestDTO);
        setAnchorEl(null);
        reloadOrderDetails();
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const isOrderStatusCompleted = orderStatus === OrderStatus.ORDER_COMPLETED;
    return (
        <>
            <Button
                aria-controls='simple-menu'
                aria-haspopup='true'
                onClick={handleClick}
                disabled={isOrderStatusCompleted}
                style={{
                    color: isOrderStatusCompleted ? 'grey' : '',
                    cursor: isOrderStatusCompleted ? 'not-allowed' : 'pointer',
                }}>
                Update Order Status
            </Button>
            <Menu id='simple-menu' anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                {availableOptions.map((statusOption) => (
                    <MenuItem key={statusOption} onClick={() => handleOrderUpdate(order.id, statusOption)}>
                        {getSelectedOptions(statusOption)}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default OrderStatusMenu;
